import { useContext, useState, useRef } from "react";
import { useRouter } from "next/router";

import {
  BareFacebookIcon,
  BareTwitterIcon,
  BarePinterestIcon,
  BareRedditIcon,
  LinkIcon,
  CheckmarkIcon,
} from "../utils/icons";
import { maxSection1 } from "../utils/styles";
import { Context } from "../context";

const BASE_URL = "https://www.appcheeta.com";

const addParams = (url, params) => {
  url = new URL(url);
  Object.keys(params).forEach((key) => url.searchParams.append(key, params[key]));
  return url;
};

const makeIncomingUrl = ({ path, source }) => {
  return addParams(BASE_URL + path, {
    utm_source: `sharer_link/${source}`,
  });
};

const makeFacebookUrl = ({ path }) => {
  return addParams("https://www.facebook.com/sharer/sharer.php", {
    u: makeIncomingUrl({ path, source: "facebook" }),
  });
};

const makeTwitterUrl = ({ path, text }) => {
  return addParams("https://twitter.com/intent/tweet", {
    url: makeIncomingUrl({ path, source: "twitter" }),
    text,
  });
};

const makePinterestUrl = ({ path, image }) => {
  return addParams("https://pinterest.com/pin/create/link/", {
    url: makeIncomingUrl({ path, source: "pinterest" }),
    media: image,
  });
};

const makeRedditUrl = ({ path, image }) => {
  return addParams("https://www.reddit.com/submit", {
    url: makeIncomingUrl({ path, source: "reddit" }),
  });
};

const makeBasicUrl = ({ path }) => {
  return makeIncomingUrl({ path, source: "basic" });
};

const SocialShare = ({ text, image }) => {
  const inputRef = useRef();
  const { asPath } = useRouter();
  const path = asPath.split("#")[0];
  const [copied, setCopied] = useState(false);
  const { theme } = useContext(Context);

  const urlParams = {
    text,
    image,
    path,
  };

  function handleClick(event) {
    inputRef.current.type = "text";
    inputRef.current.focus();
    inputRef.current.select();
    document.execCommand("copy");
    inputRef.current.type = "hidden";
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 3000);
  }
  return (
    <div className="social-share">
      <style jsx>{`
        .social-share {
          display: flex;
          align-items: center;
          position: relative;
        }
        .social-share :global(.icon) {
          fill: ${theme.primaryIconColor};
          width: 1.2rem;
          height: 1.2rem;
          cursor: pointer;
        }
        .icons a {
          margin: 0 0.5rem;
        }
        .icons a:last-child {
          margin-right: 0;
        }
        .icons a:first-child {
          margin-left: 0;
        }
        :global(.share-icon) {
          margin-right: 2rem;
        }
        .copied {
          position: absolute;
          background: ${theme.selectedBackgroundColor};
          color: ${theme.primaryTextColor};
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          display: ${copied ? "flex" : "none"};
          z-index: 10;
          align-items: center;
          font-size: 1rem;
          justify-content: center;
          text-transform: uppercase;
        }
        .copy-buffer {
        }
        .copied :global(.icon.checkmark-icon) {
          height: 1rem;
          width: 1rem;
        }
        @media screen and (min-width: ${maxSection1}px) {
          .social-share :global(.icon) {
            width: 1.5rem;
            height: 1.5rem;
          }
        }
      `}</style>
      <input className="copy-buffer" type="hidden" value={makeBasicUrl(urlParams)} ref={inputRef} />
      <div className="copied">
        <CheckmarkIcon />
        Copied to clipboard
      </div>
      <div className="icons">
        <a
          aria-label="Share with Facebook"
          target="_blank"
          rel="noopener"
          href={makeFacebookUrl(urlParams)}
        >
          <BareFacebookIcon />
        </a>
        <a
          aria-label="Share with Twitter"
          target="_blank"
          rel="noopener"
          href={makeTwitterUrl(urlParams)}
        >
          <BareTwitterIcon />
        </a>
        <a
          aria-label="Share with Pinterest"
          target="_blank"
          rel="noopener"
          href={makePinterestUrl(urlParams)}
        >
          <BarePinterestIcon />
        </a>
        <a
          aria-label="Share with Reddit"
          target="_blank"
          rel="noopener"
          href={makeRedditUrl(urlParams)}
        >
          <BareRedditIcon />
        </a>
        <a aria-label="Share">
          <LinkIcon onClick={handleClick} />
        </a>
      </div>
    </div>
  );
};

export default SocialShare;
